import type { TranslationData } from "./i18n/types";
import type {
  CampaignFull,
  CampaignMetaTags,
  CampaignTagManager,
  CampaignTokenGate,
  NewsletterConfig,
} from "./rest/types";

const MAX_TIMEOUT = 2147483647; // Maximum timeout delay

export default class Campaign {
  data: CampaignFull;
  _endTimer: number | null = null;
  _startTimer: number | null = null;

  constructor(data: CampaignFull) {
    this.data = data;
    this.runEndTimer();
    this.runStartTimer();
  }

  update(newData: CampaignFull) {
    this.data = { ...this.data, ...newData };
    this.runEndTimer();
    this.runStartTimer();
  }

  /**
   * Start a timer to dispatch an event when the campaign ends
   */
  runEndTimer(): void {
    if (this._endTimer !== null) {
      window.clearTimeout(this._endTimer);
    }
    if (!this.data.ends_at) {
      return;
    }
    const endAt = new Date(this.data.ends_at);
    let now = new Date();
    let timeLeft = endAt.getTime() - now.getTime();
    if (timeLeft <= 0) {
      document.dispatchEvent(new CustomEvent("umg:campaign:ended"));
      return;
    }
    // we can't set timeout longer than MAX_TIMEOUT
    // So we run setTimeout multiple times if the timeLeft is longer than MAX_TIMEOUT
    this._endTimer = window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent("umg:campaign:ended"));
      now = new Date();
      timeLeft = endAt.getTime() - now.getTime();
      if (timeLeft > 0) {
        this.runEndTimer();
      }
    }, Math.min(timeLeft, MAX_TIMEOUT));
  }

  runStartTimer(): void {
    if (this._startTimer !== null) {
      window.clearTimeout(this._startTimer);
    }
    if (!this.data.starts_at) {
      document.dispatchEvent(new CustomEvent("umg:campaign:started"));
      return;
    }
    const startsAt = new Date(this.data.starts_at);
    let now = new Date();
    // time in ms until the campaign starts
    let timeLeft = startsAt.getTime() - now.getTime();
    if (timeLeft <= 0) {
      // already started
      document.dispatchEvent(new CustomEvent("umg:campaign:started"));
      return;
    }

    // we can't set timeout longer than MAX_TIMEOUT
    // So we run setTimeout multiple times if the timeLeft is longer than MAX_TIMEOUT
    this._startTimer = window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent("umg:campaign:started"));
      now = new Date();
      timeLeft = startsAt.getTime() - now.getTime();
      if (timeLeft > 0) {
        this.runStartTimer();
      }
    }, Math.min(timeLeft, MAX_TIMEOUT));
  }

  /**
   * Check if the campaign has ended
   */
  hasEnded(): boolean {
    if (!this.data.ends_at) {
      return false;
    }
    return new Date(this.data.ends_at) <= new Date();
  }

  hasStarted(): boolean {
    if (!this.data.starts_at) {
      return true;
    }
    return new Date(this.data.starts_at) <= new Date();
  }

  get id(): string {
    return this.data.id;
  }

  get created_at(): string {
    return this.data.created_at;
  }

  get updated_at(): string {
    return this.data.updated_at;
  }

  get app(): string {
    return this.data.app;
  }

  get app_id(): string | null {
    return this.data.app_id;
  }

  set app_id(value: string | null) {
    console.warn(
      "setting app_id is deprecated, cms will send updated campaign"
    );
    this.data.app_id = value;
  }

  get name(): string {
    return this.data.name;
  }

  get slug(): string {
    return this.data.slug;
  }

  get meta_tags(): CampaignMetaTags {
    return this.data.meta_tags;
  }

  get tag_manager(): CampaignTagManager {
    return this.data.tag_manager;
  }

  get test(): boolean {
    return this.data.test;
  }

  get wp_url(): string | null {
    return this.data.wp_url;
  }

  get newsletter(): NewsletterConfig {
    // TODO: Deprecate. Only returns the first item in newsletters list
    return this.data.newsletter;
  }

  get newsletters(): NewsletterConfig[] {
    return this.data.newsletters;
  }

  get enabled_services(): string[] {
    return this.data.enabled_services;
  }

  get starts_at(): string | null {
    return this.data.starts_at;
  }

  get ends_at(): string | null {
    return this.data.ends_at;
  }

  get i18n(): TranslationData {
    return this.data.i18n;
  }

  get url(): string {
    return this.data.url;
  }

  get appId(): string | null {
    console.warn("appId is deprecated, use app_id instead");
    return this.data.app_id;
  }

  get trackingArtist(): string | null {
    console.warn(
      "trackingArtist is deprecated, use tag_manager.artist instead"
    );
    return this.data.tag_manager.artist;
  }

  get trackingGenre(): string | null {
    console.warn("trackingGenre is deprecated, use tag_manager.genre instead");
    return this.data.tag_manager.genre;
  }

  get trackingDivision(): string | null {
    console.warn(
      "trackingDivision is deprecated, use tag_manager.division instead"
    );
    return this.data.tag_manager.division;
  }

  get newsletterEnabled(): boolean {
    console.warn(
      "newsletterEnabled is deprecated, use newsletter.enabled instead"
    );
    return this.data.newsletter.enabled;
  }

  enabledServices(): string[] {
    console.warn("enabledServices is deprecated, use enabled_services instead");
    return this.data.enabled_services;
  }

  get token_gate(): CampaignTokenGate {
    return this.data.token_gate;
  }

  get token_gated_campaign_url(): string | null {
    return this.data.token_gated_campaign_url;
  }

  get token_gate_url(): string | null {
    return this.data.token_gate_url;
  }

  get is_token_gate(): boolean {
    return this.data.is_token_gate;
  }

  get parent_id(): string | null {
    return this.data.parent_id;
  }

  // Fields from old campiagn info
}

// -interface CampaignInfo
// -  extends Record<
// -    string,
// -    string | null | boolean | NewsletterConfig | string[]
// -  > {
// -  id: string | null;
// -  app: string | null;
// -  appId: string | null;
// -  trackingArtist: string | null;
// -  trackingGenre: string | null;
// -  trackingDivision: string | null;
// -  url: string | null;
// -  newsletterEnabled: boolean;
// -  newsletter: NewsletterConfig;
// -  enabledServices: string[];
// -}
